import clsx from 'clsx'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import useInfiniteScroll from 'react-infinite-scroll-hook'

import BettingUser from '@/components/icons/betting-user.svg'
import Clock from '@/components/icons/clock.svg'
import Empty from '@/components/icons/empty.svg'
import Refresh from '@/components/icons/refresh.svg'
import Segmented from '@/components/ui/segmented'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import {
  formatNumber,
  formatPercent,
  formatPrice,
  formatRatio,
  formatTime,
} from '@/lib/formatter'
import {
  getPaginationDisplayType,
  PaginationDisplayType,
} from '@/model/pagination'
import { useReadMineActivityLogInfiniteQuery } from '@/redux/activity-log'
import {
  useReadBettingRecordsQuery,
  useReadBettingUserOverviewQuery,
} from '@/redux/competition-betting'
import { useAppSelector } from '@/redux/utils'

import BettingHistory from './betting-history'

export interface BettingProps {
  id: number
}

export default function Betting({ id }: BettingProps) {
  const token = useAppSelector((state) => state.global.token)
  const [activeTab, setActiveTab] = useState<'trading' | 'betting'>('betting')

  //const { data: bettingOverviewData } = useReadBettingOverviewQuery(id, { skip: token === undefined })
  const { data: bettingUserOverviewData } = useReadBettingUserOverviewQuery(
    id,
    { skip: token === undefined },
  )
  const { data: bettingRecordsData } = useReadBettingRecordsQuery(id, {
    skip: token === undefined,
  })

  const {
    data: activityLogs,
    fetchNextPage,
    isLoading: isLoadingActivityLogs,
    isFetching: isFetchingActivityLogs,
    isFetchingNextPage: isFetchingNextActivityLogs,
    hasNextPage,
  } = useReadMineActivityLogInfiniteQuery(id, { skip: token === undefined })

  const [infiniteRef] = useInfiniteScroll({
    loading: isFetchingActivityLogs,
    hasNextPage,
    onLoadMore: fetchNextPage,
  })

  const bettingView = () => {
    return (
      <div>
        <div>
          <div className="text-2xl font-semibold">Subscription overview</div>
          <div className="mt-2 text-sm text-tertiary-foreground">
            Update time:{' '}
            {formatTime('MM/DD HH:mm:ss')(
              bettingUserOverviewData?.updatedAt ?? 0,
            )}
          </div>
        </div>
        <div className="mt-6">
          <div className="p-5 w-83 rounded-2xl border border-white/12 bg-white/8 backdrop-blur-[100px]">
            <div className="grid grid-cols-3 gap-8 truncate text-sm text-secondary-foreground leading-5.5">
              <div>Total Pts</div>
              <div>Current Pts</div>
              <div>PNL</div>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-8 font-semibold text-base leading-6">
              <div>
                {formatNumber()(bettingUserOverviewData?.totalPoints)} Pts
              </div>
              <div>
                {formatNumber()(bettingUserOverviewData?.bettingPoints)} Pts
              </div>
              <div>
                {formatPercent()((bettingUserOverviewData?.pnl ?? 0) / 10000)}
              </div>
            </div>
            {(bettingUserOverviewData?.totalPoints ?? 0) < 100 && (
              <div className="mt-3">
                <div
                  className="ml-3 w-2 h-1 bg-white/8"
                  style={{ clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' }}
                ></div>
                <div className="px-2 py-1 bg-white/8 rounded-md text-xs leading-4.5">
                  You need at least{' '}
                  <span className="font-semibold text-yellow-400">100 pts</span>{' '}
                  to place a bet.
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-10">
          <div className="text-2xl font-semibold leading-8">
            Subscription Record
          </div>
          <div
            className={clsx(
              'p-5 mt-6 w-83  rounded-2xl border border-white/12 bg-white/8 backdrop-blur-[100px] overflow-y-auto',
              bettingUserOverviewData?.isCompetitor ? 'h-110' : 'h-130',
              (!bettingRecordsData || bettingRecordsData?.length === 0) ? 'flex justify-center items-center' : '',
            )}
          >
            {(!bettingRecordsData || bettingRecordsData?.length === 0) && (
              <div className="flex flex-col gap-3 items-center justify-center">
                <Empty className="size-20" />
                <div className="text-xs text-tertiary-foreground text-center">
                  You haven’t subscribed any yet. <br />Start subscribing now!
                </div>
              </div>
            )}
            {bettingRecordsData && bettingRecordsData?.length > 0 && (
              <div className="flex flex-col gap-4">
                {bettingRecordsData?.map((record, index) => {
                  return (
                    <div
                      className="w-73 rounded-2xl overflow-hidden"
                      key={index}
                    >
                      <div className="h-9.5 p-3 bg-white/12 flex justify-between items-center">
                        <div className="flex items-center gap-1">
                          <BettingUser className="size-4" />
                          <div className="w-32 truncate">{record.nickname}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <div>
                            <Refresh className="size-3" />
                          </div>
                          <div className="text-xs text-tertiary-foreground truncate">
                            {formatTime('MM/DD HH:mm:ss')(record.updatedAt)}
                          </div>
                        </div>
                      </div>
                      <div className="py-2 px-3 flex flex-col gap-3 bg-[#FFFFFF14] text-xs text-secondary-foreground leading-4.5">
                        <div className="flex justify-between items-center">
                          <div>
                            Points subscribed: {formatNumber()(record.pointsBet)} pts
                          </div>

                          <TooltipProvider key={'title'}>
                            <Tooltip>
                              <TooltipTrigger>
                                <div className="size-5 flex justify-center items-center text-tertiary-foreground hover:bg-white/8 rounded-sm cursor-pointer">
                                  <Clock className="size-3" />
                                </div>
                              </TooltipTrigger>
                              <TooltipContent
                                side="left"
                                sideOffset={4}
                                className="bg-[#222224]"
                              >
                                <BettingHistory
                                  id={id}
                                  competitorId={record.competitorID}
                                />
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </div>
                        <div>
                          PNL: {formatPercent()((record.pnl ?? 0) / 10000)}
                        </div>
                        <div>
                          Live Redeem: {formatNumber()(record.liveRedeem)} pts
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  const activityLogsDisplayType = getPaginationDisplayType({
    data: activityLogs,
    isLoading: isLoadingActivityLogs,
    isFetching: isFetchingActivityLogs,
    isFetchingNextPage: isFetchingNextActivityLogs,
  })

  const rank = bettingUserOverviewData?.userCompetitionData?.rank

  const tradingView = () => {
    return (
      <div>
        <div>
          <div className="flex justify-between">
            <div className="text-2xl font-semibold">Trading Dashboard</div>
            {rank && (
              <div>
                <span className="font-bold font-urbanist text-[1.75rem] bg-clip-text text-fill-transparent bg-linear-to-r from-gradient-blue to-gradient-purple-light">
                  {rank}
                </span>
                <span className="text-primary">th</span>
              </div>
            )}
          </div>
          <div className="flex justify-between mt-2 text-sm text-tertiary-foreground">
            <div>
              Update time:{' '}
              {formatTime('MM/DD HH:mm:ss')(
                bettingUserOverviewData?.userCompetitionData?.updatedAt ?? 0,
              )}
            </div>
            <div>My Ranking</div>
          </div>
        </div>
        <div className="mt-6">
          <div className="p-6 w-83 rounded-2xl border border-white/12 bg-white/8 backdrop-blur-[100px]">
            <div className="grid grid-cols-3 gap-8 truncate text-xs text-secondary-foreground leading-5.5">
              <div>Balance</div>
              <div>Sharpe Ratio</div>
              <div>MDD</div>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-8 font-semibold text-base leading-6">
              <div>
                {formatPrice()(
                  bettingUserOverviewData?.userCompetitionData?.balance ?? 0,
                )}
              </div>
              <div>
                {formatRatio()(
                  bettingUserOverviewData?.userCompetitionData?.sharpeRatio ?? 0,
                )}
              </div>
              <div>
                {formatPercent()(
                  bettingUserOverviewData?.userCompetitionData?.mdd ?? 0,
                )}
              </div>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-8 truncate text-xs text-secondary-foreground leading-5.5">
              <div>ROI</div>
              <div>PNL</div>
              <div></div>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-8 font-semibold text-base leading-6">
              <div>
                {formatPercent()(
                  bettingUserOverviewData?.userCompetitionData?.roi ?? 0,
                )}
              </div>
              <div>
                {formatPrice()(
                  bettingUserOverviewData?.userCompetitionData?.pnl ?? 0,
                )}
              </div>
              <div></div>
            </div>
          </div>
        </div>
        <div className="mt-10">
          <div className="text-2xl font-semibold leading-8">Activity Log</div>
          <div className="p-5 mt-6 w-83 h-94 rounded-2xl border border-white/12 bg-white/12 backdrop-blur-[100px]">
            <div className="flex flex-col gap-6">
              {activityLogs
                ?.pages
                .flatMap(({ rows }) => rows)
                .map(({ id: logId, content, createdAt }) => (
                  <div
                    key={logId}
                    className="flex justify-between items-center"
                  >
                    <div className="text-xs">{content}</div>
                    <div className="text-tiny text-muted-foreground">
                      {formatTime('MM/DD HH:mm')(createdAt)}
                    </div>
                  </div>
                ))}
              {activityLogsDisplayType === PaginationDisplayType.Empty && (
                <div className="pt-20 flex flex-col gap-3 items-center justify-center">
                  <Empty className="size-20" />
                  <div className="text-xs text-tertiary-foreground">No Activity yet</div>
                </div>
              )}
              {hasNextPage && (
                <Loader2 className="animate-spin" ref={infiniteRef} />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-83">
      {bettingUserOverviewData?.isCompetitor && (
        <Segmented
          options={[{
            label: 'Trading',
            value: 'trading',
          }, {
            label: 'Subscribe',
            value: 'betting',
          }]}
          value={activeTab}
          onChange={setActiveTab}
          className="w-full mb-6"
        />
      )}
      {(activeTab === 'betting' || !bettingUserOverviewData?.isCompetitor) &&
        bettingView()}
      {activeTab === 'trading' &&
        bettingUserOverviewData?.isCompetitor &&
        tradingView()}
    </div>
  )
}
